import { FEED_KIND, type FeedKind, isPreparedFeed2Id, isCupisForkId } from './feedContext';
/**
 * Строка ленты: из объекта вилки — в числа, которые человек вбивает руками.
 *
 * Считает это клиент, а не сервер, и причина ровно одна: банк, порог, курс и
 * допущенные лимиты — регуляторы оператора. Ходить за пересчётом на сервер на
 * каждое движение ползунка означало бы, что регулятор двигается рывками, а
 * значит им не пользуются.
 *
 * Главное число строки — ПРИБЫЛЬ при том объёме, где она максимальна, а не
 * процент. Считается в рублях банка: курс, порог и касса оператора живут в
 * рублях. На экран и в буфер уходит валюта площадки: рубли у ру-конторы,
 * доллары у зарубежной, центы и шеры у биржи. Показать рубли банка на BC.Game
 * значило бы отправить человека считать курс в уме на скорости (23.08).
 *
 * Процент сравнивает вилки между собой и живёт в карточке, рядом с глубиной
 * стакана, где виден его настоящий вес: 27% на стакане в шесть долларов — это
 * несколько долларов прибыли, а не «двадцать семь».
 */

import { Decimal } from 'decimal.js';

import {
  type Leg,
  type RoundDir,
  type VolumeQuote,
  bestVolume,
  bookmakerTicket,
  effectiveOdds,
  fillLeg,
  layOdds,
  layTicket,
  payout as stakePayout,
  quoteAtVolume,
  quoteFromAnchor,
  sharesAfterFee,
  sharesShown,
  SPORTS_FEE_RATE,
} from './calc';
import { alignFork } from './align';
import { soleInitiator } from './initiator';
import { healShareFork, isShareLeg, isShareVenue, lookLikeShareCents } from './shareVenue';
import { marketParts, marketPrefix, namedSource, type Fork, type ForkLeg } from './protocol';
import { marketPeriod } from './sports';
import { participantText } from './participantText';
import { deadPrice } from '../poly/market';

/** Минимальный ордер Polymarket: мельче площадка не примет. */
export const MIN_EXCHANGE_ORDER_USD = 5;

export interface RowLeg {
  /** Fee model used for this exact rendered payout; UI-only, never guessed from stale raw quotes. */
  payoutModel?: { kind: 'shares'; rate: string; exponent: number; assumed: boolean }
    | { kind: 'profit'; rate: string };
  source: string;
  /** Написание участника на этой площадке — по нему человек ищет ставку. */
  name: string;
  /** Оба участника, как их пишет ЭТА площадка: по ним прицел находит строку. */
  who: string[];
  side: string;
  unit: 'share' | 'money';
  cur: string;
  /** Крупное число ноги: центы у биржи, рубли у ру-конторы, доллары у зарубежной. */
  headline: string;
  /** Мелкое рядом: количество штук у биржи, кэф у конторы. */
  detail: string;
  /** Сколько денег уходит в эту ногу, в рублях. */
  rub: number;
  /** Сколько вернётся, если зайдёт именно эта нога, в рублях. */
  payout: number;
  /** Доллары ноги: их спишет зарубежная площадка. `null` у ру-конторы. */
  usd: number | null;
  /**
   * Сумма покупки по рынку, доллары: столько уйдёт по средней цене нашего
   * стакана за те же `shares`. Не `usd` — тот считан по худшему уровню для
   * лимитки, и вбитый в Market он купил бы больше шаров, чем ждёт вторая нога.
   */
  marketUsd?: number | null;
  /** Наш стакан ноги в долларах (цена, шары), от лучшей цены к худшей. */
  book?: readonly (readonly [string, string])[];
  /** Цена шара, выше которой вилки нет при второй ноге этой строки. */
  marketDead?: string;
  shares: number | null;
  cents: number | null;
  odds: string;
  /** Нога «против» на бирже: человек принимает чужую ставку, как букмекер. */
  lay: boolean;
  /** Что ввести на бирже за ногу «против». Со счёта при этом уйдёт `rub` — риск. */
  layStake: number | null;
  /**
   * Потолок этой ноги и откуда он взялся. `balance` — баланс счёта человека,
   * в валюте `cur` (нет поля — в валюте кассы ноги).
   */
  cap: { kind: 'book' | 'limit' | 'assumed' | 'balance' | 'freebet'; value: number | null; cur?: 'RUB' | 'USD' };
  /** Стакан биржи по цене вилки не вмещает плановые шары. */
  thin?: boolean;
  /**
   * Координаты ордера на бирже: токен исхода, шаг цены, минимальный ордер.
   * `null` у конторы и у биржевой ноги зеркала — там покупать нечем и нечего.
   */
  asset: string | null;
  tick: number | null;
  minSize: number | null;
  /** Точность входа: по ней считается надбавка за навигацию. */
  precision: string;
  url: string | null;
  /** Номер события площадки: дверь матча, когда лента принесла раздел. */
  eid?: string | null;
  /** Тройка конторы для водителя. Нет у биржи и у ноги без vendor id. */
  sel?: string | null;
  /** Период рынка: `M` / `P4`. Нет на ноге — берём из `fork.market`. */
  period?: string | null;
  /** Код рынка вилки: WIN / TTL / HCP. Пусто у старой ноги. */
  kind?: string | null;
  /** Линия тотала или форы из ключа, без знака. */
  line?: string | null;
  /** Full source market coordinates; line alone loses units and the anchor side. */
  marketKey?: string;
  sport?: string;
  eventLine?: 'live' | 'prematch';
  eventStart?: number;
  initiator?: boolean;
  initiatorCount?: number | null;
  /**
   * Человек вбил кэф или центы сам. Пока замок жив, лента это число не
   * затирает — иначе в карточке прыгает сумма, а в контору уходит не то.
   */
  ownOdds?: boolean;
  /** Кэф с кадра, если свой другой. Рядом мелким: «лента 3.830». */
  oddsLive?: string;
  /** Центы с кадра у биржи. */
  centsLive?: number | null;
  /**
   * Своя комиссия ноги, процентами. Показанный кэф не меняется: режется
   * выплата, как у биржи: комиссия режет профит, не кэф.
   */
  ownFee?: boolean;
  /** Набитый процент комиссии, если свой. */
  feePct?: string;
  /**
   * Валюта кассы, если человек переключил. Каталог площадки при этом
   * остаётся собой: Thunderpick в реестре зарубежный, а счёт может быть
   * в рублях.
   */
  ownCur?: 'RUB' | 'USD';
  /**
   * Фрибет (ставка не возвращается): со счёта эта сумма не уходит,
   * выплата = сумма × (кэф − 1). Как у SNR-фрибета.
   */
  freebet?: boolean;
}

export interface Row {
  /** Параметры расчёта строки сохраняются при правке кэфа в карточке. */
  transferBudget?: TransferBudget;
  fork: Fork;
  legs: RowLeg[];
  /** Прибыль в рублях при выбранном объёме. Главное число строки. */
  profit: number;
  /** Сколько всего уходит со счетов и сколько вернётся при любом исходе. */
  staked: number;
  payout: number;
  /** Процент. Живёт в карточке: в ленте он вводит в заблуждение. */
  roiPct: number;
  /** Exact net percentage before display formatting. */
  netRoiExact?: string;
  /** Чем ограничен объём: стакан, лимит конторы, банк, баланс счёта или допущение. */
  cappedBy: 'стакан' | 'лимит' | 'банк' | 'баланс БК' | 'баланс' | 'фрибет' | 'допущение' | null;
  /** Баланс, в который упёрся объём: контора, сумма, валюта. */
  capBalance?: { source: string; value: number; cur: 'RUB' | 'USD' };
  /** Право вести: серверное знание плюс наши потолок и темп. */
  lead: boolean;
  /** Чего не хватило ЭТОЙ строке до права вести. Пусто — не хватило ничего. */
  blockers: string[];
  /**
   * Насколько строке можно верить, одним словом (07.08, владелец).
   *
   * `dead` — правила гейта говорят «цены, из которых сложена вилка, скорее
   * всего уже неверны»: замёрзшая цена, кэф без согласия рынка, опечатка.
   * Такая строка не имеет права стоять наверху ленты — по ней проигрывают
   * деньги, и сортировка топит её последней (см. `filters.compare`).
   *
   * `fast` — вилка живая, но обычно уходит быстрее, чем человек ставит две
   * ноги. Это темп, а не угроза: место в ленте она держит по деньгам.
   */
  doubt: 'dead' | 'fast' | null;
  /**
   * Чего нет у источника вообще: живучесть пар не измерена, лимиты не названы.
   * Отдельно от `blockers`, потому что показывается один раз над лентой, а не
   * повторяется в каждой строке — см. `gapsOf`.
   */
  gaps: string[];
}

const num = (value: string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Шаг рынка из стакана, если лента не прислала `tick`.
 *
 * Без шага купон Limit не ставит `price`: ошибка на порядок дороже пустого
 * поля. Стакан уже лежит на сетке площадки — по нему шаг видно.
 *
 * 0.1 не берём: 50¢ и 60¢ лежат и на центе. Грубый шаг превратил бы
 * 51.3 ¢ карточки в 50 — площадка принимает 51.
 */
export function tickFromBook(book: [string, string][] | undefined): number | null {
  if (!book || book.length === 0) return null;
  const prices: Decimal[] = [];
  for (const level of book) {
    const price = new Decimal(level[0] ?? '');
    if (!price.isFinite() || price.lte(0)) continue;
    prices.push(price);
  }
  if (prices.length === 0) return null;
  const steps = [new Decimal('0.01'), new Decimal('0.001'), new Decimal('0.0001')];
  for (const step of steps) {
    if (prices.every((price) => price.mod(step).isZero())) return step.toNumber();
  }
  return null;
}

/**
 * Шаг цены биржевой ноги для купона. Явный `tick` ленты важнее стакана.
 * Без стакана у зеркала всё равно нужен центовый шаг: иначе Limit оставит
 * чужие 50¢ из `betUrl`, а карточка уже показывает 51.3.
 */
export function exchangeTick(leg: ForkLeg, isBook: boolean, exchange: boolean): number | null {
  const named = num(leg.tick);
  if (named !== null && named > 0) return named;
  if (isBook) {
    const inferred = tickFromBook(leg.book);
    if (inferred !== null) return inferred;
  }
  if (exchange && isShareVenue(leg.s)) return 0.01;
  return null;
}

/** Цена к сетке площадки, вниз: дороже показанного не вбиваем. */
export function snapToTick(price: Decimal, tick: number): Decimal | null {
  if (!(tick > 0) || !price.isFinite() || price.lte(0) || price.gte(1)) return null;
  const step = new Decimal(tick);
  const ticks = price.div(step).toDecimalPlaces(0, Decimal.ROUND_DOWN);
  if (ticks.lte(0)) return null;
  const snapped = ticks.times(step);
  if (snapped.lte(0) || snapped.gte(1)) return null;
  return snapped;
}

/**
 * Поле `share` отстало от кэфа ленты: 0.50 при кэфе 1.951 — это 50¢ из
 * чужого стакана, не цена вилки. Строка и купон идут за кэфом.
 */
function shareFieldStale(leg: ForkLeg): boolean {
  const named = num(leg.share);
  const odds = num(leg.odds);
  const fromOdds = odds !== null && odds > 1 && !lookLikeShareCents(odds) ? 1 / odds : null;
  return (
    named !== null &&
    named > 0 &&
    named < 1 &&
    fromOdds !== null &&
    Math.abs(named - fromOdds) > 0.001
  );
}

export type RoundStep = 1 | 5 | 10 | 100 | 1000;
export type RoundWho = 'book' | 'coarse';
/** Чем набивают биржевую сумму: баксы расхода или шеры в купон. */
export type ExchangeStake = 'usd' | 'share';

export interface TransferBudget {
  amount: string;
  currency: 'RUB' | 'USD';
}

export interface RowInput {
  feedKind?: FeedKind;
  transferBudget?: TransferBudget;
  bank: number;
  fx: number;
  /**
   * Баланс счёта по конторам: код → сколько на счёте. Обе ноги ужимаются,
   * чтобы ни одна не вышла за свой баланс. Валюта — `capsCur`, иначе касса ноги.
   */
  caps: Record<string, number>;
  /** Валюта баланса из `caps`, если известна (касса, карандаш, бот). */
  capsCur?: Record<string, 'RUB' | 'USD'>;
  /**
   * Фрибет SNR по конторам: код → сумма. Нога этой конторы считается фрибетом,
   * объём не выше суммы. Замок карточки (`free`) важнее этой карты.
   */
  freebets?: Record<string, number>;
  /** Валюта фрибета из `freebets`. Нет ключа — валюта кассы ноги. */
  freebetsCur?: Record<string, 'RUB' | 'USD'>;
  /** Лимит из купона конторы до перезагрузки вкладки, в валюте кассы ноги. */
  couponCaps?: Record<string, number>;
  /** Измеренный темп оператора, секунды. null — замеров ещё нет. */
  tempo: number | null;
  /**
   * Округлять конторскую ногу. Нет поля — десятки: так лента резала и раньше,
   * и старые тесты «до десятки» остаются собой.
   */
  roundStep?: RoundStep;
  /** Вниз — не вылезем за банк. Нет поля — вниз. */
  roundDir?: RoundDir;
  /** Кто получает круглую сумму. Нет поля — контора. */
  roundWho?: RoundWho;
  /**
   * Набитое плечо этой вилки. Банк его не затирает: кэф уехал — вторая
   * нога и плюс пересчитываются вокруг этого числа.
   * На бирже `as` говорит, чем набито поле: баксы расхода или шеры купона.
   */
  lock?: { forkId: string; source: string; form: string; as?: ExchangeStake };
  /**
   * Свои кэфы / центы этой вилки. Ключ — код площадки, значение — то, что
   * человек набил: кэф у конторы, центы (вероятность × 100) у биржи.
   */
  odds?: OddsLock;
  /**
   * Комиссия ноги, процентами (0–100). Контора — из выплаты, биржа —
   * ставка `feeRate` вместо расписания рынка.
   */
  fees?: FeeLock;
  /** Валюта кассы ноги: рубли или доллары, поверх каталога площадки. */
  curs?: CurLock;
  /** Нога-фрибет этой вилки: ставка не из кармана, выплата без возврата. */
  free?: FreeLock;
  /** Третья нога руками. Только если в кадре две. */
  hand?: HandLock;
  /**
   * Стол калькулятора. Вилка в минусе считается на тот же банк, а не сжимается
   * до шага кассы (0.21 $ / 0.11 $), и вторая нога круглая, только пока исходы
   * почти равны.
   */
  hold?: boolean;
}

/** Свой кэф одной раскрытой вилки. Живёт рядом с замком суммы. */
export type OddsLock = { forkId: string; by: Record<string, string> };

/** Своя комиссия одной раскрытой вилки. Тот же срок, что у кэфа. */
export type FeeLock = { forkId: string; by: Record<string, string> };

/** Своя валюта ноги. Только ₽ и $: другого курса у банка нет. */
export type CurLock = { forkId: string; by: Record<string, 'RUB' | 'USD'> };

/** Фрибет на одной ноге раскрытой вилки. */
export type FreeLock = { forkId: string; source: string };

/** Третья нога, которую человек дописал сам. */
export type HandLock = { forkId: string; odds: string };

/** Код площадки ноги, которую набили руками. Слова на экране — «руками». */
export const HAND_SOURCE = 'hand';

/**
 * Разобрать набитое. Пустое и мусор — `null`, замок не ставим.
 * Контора: кэф строго больше единицы. Биржа: центы в (0, 100).
 */
/**
 * Комиссия конторы / биржи: 1 + (к − 1)×(1 − c). Кэф на экране остаётся
 * тем, что вводят в кассу; режется выплата. `effectiveOdds` из calc.ts —
 * другая формула (доля ставки), её сюда не подставлять.
 */
export function commissionOdds(odds: string, feePct: string): Decimal {
  const c = new Decimal(feePct).div(100);
  if (c.lte(0)) return new Decimal(odds);
  if (c.gte(1)) return new Decimal(1);
  return new Decimal(1).plus(new Decimal(odds).minus(1).times(new Decimal(1).minus(c)));
}

/**
 * Разобрать комиссию. Пустое и мусор — `null`. Ноль законен: биржа без
 * комиссии, и человек это видит на сайте.
 */
export function readOwnFee(raw: string): string | null {
  const text = raw.trim().replace(/\s/g, '').replace(',', '.').replace(/%/g, '');
  if (text === '' || text === '.') return null;
  try {
    const n = new Decimal(text);
    if (!n.isFinite() || n.lt(0) || n.gte(100)) return null;
    return text;
  } catch {
    return null;
  }
}

function lockOf<T extends { forkId: string }>(
  lock: T | undefined,
  forkId: string,
): T | undefined {
  return lock?.forkId === forkId ? lock : undefined;
}

function feePctOf(leg: ForkLeg, fees: FeeLock | undefined): string | null {
  if (fees === undefined) return null;
  const raw = fees.by[leg.s];
  return raw === undefined ? null : readOwnFee(raw);
}

function curOf(leg: ForkLeg, curs: CurLock | undefined): string {
  const own = curs?.by[leg.s];
  if (own === 'RUB' || own === 'USD') return own;
  return leg.cur ?? (isExchange(leg) ? 'USD' : 'RUB');
}

function rubForm(cur: string, fx: number): boolean {
  return isRubCur(cur) || !(fx > 0);
}

export function readOwnQuote(raw: string, kind: 'odds' | 'cents'): string | null {
  const text = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (text === '' || text === '.') return null;
  try {
    const n = new Decimal(text);
    if (!n.isFinite()) return null;
    if (kind === 'odds') return n.gt(1) ? text : null;
    return n.gt(0) && n.lt(100) ? text : null;
  } catch {
    return null;
  }
}

/**
 * Нога протокола в ногу расчёта. Биржевая приводится к рублю по объёму.
 *
 * Курсом умножается ОБЪЁМ уровня, а не цена, и это не вопрос вкуса. Кэф
 * безразмерен: шер за 44 цента даёт 2.27 к одному хоть в долларах, хоть в
 * рублях. Умножь мы цену, кэф внутри расчёта стал бы 1/(0.44 × 79) — в
 * восемьдесят раз меньше настоящего, и вилка перестала бы быть вилкой ровно
 * там, где считает машина, а не человек.
 *
 * Поэтому шер заменяется расчётной единицей «рубль выплаты»: их в шере ровно
 * столько, сколько рублей в долларе. Цена такой единицы численно равна долларовой
 * цене шера, расход выходит в рублях, кэф остаётся собой. Настоящие шеры
 * восстанавливаются делением на курс.
 */
type LegCap = {
  kind: 'limit' | 'balance' | 'freebet';
  value: number;
  cur: 'RUB' | 'USD';
  rub: Decimal;
};

/**
 * Какую ногу считать фрибетом. Замок карточки важнее суммы в счёте.
 * Две конторы с фрибетом — та, где кэф выше: так SNR конвертируется лучше.
 */
function bookFreeOf(
  forkId: string,
  legs: readonly ForkLeg[],
  input: Pick<RowInput, 'free' | 'freebets'>,
): FreeLock | undefined {
  const card = lockOf(input.free, forkId);
  if (card !== undefined) return card;
  const amounts = input.freebets;
  if (amounts === undefined) return undefined;
  let pick: { source: string; odds: Decimal } | null = null;
  for (const leg of legs) {
    const amt = amounts[leg.s];
    if (amt == null || !Number.isFinite(amt) || amt <= 0) continue;
    let odds: Decimal;
    try {
      odds = new Decimal(leg.odds);
    } catch {
      continue;
    }
    if (!odds.isFinite() || odds.lte(1)) continue;
    if (pick === null || odds.gt(pick.odds)) pick = { source: leg.s, odds };
  }
  return pick === null ? undefined : { forkId, source: pick.source };
}

/**
 * Самый тесный потолок ноги в рублях расчёта: лимит из кадра, лимит из купона,
 * баланс счёта или сумма фрибета. Фрибет со счёта не уходит — баланс кассы
 * эту ногу не режет. Каждый потолок в своей валюте; без курса долларовый
 * не считаем.
 */
function legCap(leg: ForkLeg, input: RowInput): LegCap | null {
  const legCur: 'RUB' | 'USD' = leg.cur === 'RUB' ? 'RUB' : 'USD';
  let best: LegCap | null = null;
  const add = (kind: LegCap['kind'], value: number | null | undefined, cur: 'RUB' | 'USD'): void => {
    if (value == null || !Number.isFinite(value) || value <= 0) return;
    if (cur !== 'RUB' && !(input.fx > 0)) return;
    const rub = cur === 'RUB' ? new Decimal(value) : new Decimal(value).times(input.fx);
    if (best === null || rub.lt(best.rub)) best = { kind, value, cur, rub };
  };
  const exchange = leg.book !== undefined && leg.book !== null && leg.book.length > 0;
  if (!exchange && leg.cap?.kind === 'limit') add('limit', num(leg.cap.value), legCur);
  add('limit', input.couponCaps?.[leg.s], legCur);
  if (input.free?.source === leg.s) {
    add('freebet', input.freebets?.[leg.s], input.freebetsCur?.[leg.s] ?? input.capsCur?.[leg.s] ?? legCur);
  } else {
    add('balance', input.caps[leg.s], input.capsCur?.[leg.s] ?? legCur);
  }
  return best;
}

function calcLeg(leg: ForkLeg, input: RowInput, fees?: FeeLock): Leg | null {
  const fx = input.fx;
  const feePct = feePctOf(leg, fees);
  if (leg.book && leg.book.length > 0) {
    if (fx <= 0) return null;
    const tight = legCap(leg, input);
    return {
      kind: 'book',
      asks: leg.book.map(([price, size]) => [
        price,
        new Decimal(size).times(fx).toString(),
      ]) as [string, string][],
      minOrder: new Decimal(MIN_EXCHANGE_ORDER_USD).times(fx).toString(),
      // Пусто — расписание рынка (обычно 5 %). Ноль — человек видит на сайте
      // рынок без комиссии и не хочет, чтобы мы её придумали.
      ...(feePct !== null ? { feeRate: new Decimal(feePct).div(100).toString() }
        : leg.feeRate !== undefined ? { feeRate: leg.feeRate } : {}),
      ...(leg.feeExponent !== undefined ? { feeExponent: leg.feeExponent } : {}),
      ...(leg.feeModel === 'min' ? { feeModel: 'min' as const } : {}),
      ...(tight !== null ? { limit: tight.rub.toString() } : {}),
    };
  }
  const tight = legCap(leg, input);
  const raw = leg.lay === true ? layOdds(leg.odds).toString() : leg.odds;
  const cap = tight === null ? null : tight.rub.toString();
  // Фрибет SNR: в кассу не возвращается ставка, только выигрыш. В раскладке
  // множитель — (кэф − 1), затем комиссия с выигрыша, не формула cash-odds.
  // Иначе 1 + ((к−1)−1)×(1−c) врёт уже на кэфе 2.10.
  if (input.free?.source === leg.s) {
    const win = new Decimal(raw).minus(1);
    if (win.lte(0)) return null;
    const after =
      feePct !== null ? win.times(new Decimal(1).minus(new Decimal(feePct).div(100))) : win;
    if (after.lte(0)) return null;
    return { kind: 'fixed', odds: after.toString(), limit: cap };
  }
  return {
    kind: 'fixed',
    // Нога «против» приводится к обычной ставке размером в риск: кэф
    // 1 + (1 − fee)/(кэф − 1). Дальше распределение банка работает как всегда,
    // и «ставкой» ноги везде считается риск — то, что реально уйдёт со счёта.
    // Своя комиссия режет выплату, показанный кэф не трогает.
    odds: feePct !== null ? commissionOdds(raw, feePct).toString() : raw,
    limit: cap,
  };
}

/**
 * Цена шара в центах для показа: целое остаётся целым, дробное — с десятой.
 *
 * У нашего стакана цена всегда целый цент — тик биржи, — и там десятой доли не
 * возникает никогда. У ноги зеркала цена посчитана из кэфа и в цент не попадает:
 * кэф 11.950 — это 8.37 ¢, и округление до 9 ¢ придумало бы человеку семь
 * процентов расхода и разошлось бы с кэфом, который стоит рядом.
 */
export const centsText = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

/** Рублёвая касса: в форму вводят рубли, не доллары банка. */
export function isRubCur(cur: string): boolean {
  return cur === 'RUB' || cur === '₽';
}

/** Шаг показанной цены шара: десятая доля цента — так котируют дешёвые рынки. */
const PRICE_STEP = 3;

/**
 * Цена шара биржевой ноги, у которой нет НАШЕГО стакана. `null` — не биржа или
 * цену не из чего взять.
 *
 * Поле `share` — то, что назвал источник. Запасной путь — тот же кэф, повёрнутый
 * обратно (цена = 1/кэф): это не новое утверждение о рынке, а одно и то же число
 * в других единицах. Цена вне (0,1) отбрасывается: за доллар шар не продают, и
 * такая нога скорее сломана, чем дорога.
 */
function sharePrice(leg: ForkLeg): Decimal | null {
  if (leg.unit !== 'share' && !isShareVenue(leg.s)) return null;
  const named = num(leg.share);
  const odds = num(leg.odds);
  // 69.1 в кэфе полика — 69¢, не 1.4¢ из 1/69.1. Иначе «цена 1» и ROI +266%.
  if (odds !== null && lookLikeShareCents(odds)) {
    const cents = named !== null && named > 0 && named < 1 ? named : odds / 100;
    if (cents > 0 && cents < 1) {
      return new Decimal(cents).toDecimalPlaces(PRICE_STEP, Decimal.ROUND_UP);
    }
  }
  const fromOdds = odds !== null && odds > 1 ? 1 / odds : null;
  // Шар дороже доллара — поломка, не вчерашняя цена: запасным путём её не
  // замазывают. А вот 50 ¢ при кэфе 4.000 — отставшее поле, идём за кэфом.
  const stale =
    named !== null &&
    named > 0 &&
    named < 1 &&
    fromOdds !== null &&
    Math.abs(named - fromOdds) > 0.001;
  const price = stale ? fromOdds : (named ?? fromOdds);
  if (price === null || price <= 0 || price >= 1) return null;
  // Вверх, как и цена нашего стакана: цена — это расход, округление против нас.
  return new Decimal(price).toDecimalPlaces(PRICE_STEP, Decimal.ROUND_UP);
}

/** Потолок объёма: банк оператора. Больше банка мы не предлагаем никогда. */
const ceilingOf = (input: RowInput): string => String(Math.max(1, input.bank));

/** Биржа: есть наш стакан или цена шара. Контора — всё остальное. */
const isExchange = (leg: ForkLeg): boolean =>
  Boolean(leg.book && leg.book.length > 0) || sharePrice(leg) !== null;

/** Биржа в форме: центы, не кэф. Площадку смотрим даже без unit=share. */
const quoteKind = (leg: ForkLeg): 'odds' | 'cents' => (isShareLeg(leg) ? 'cents' : 'odds');

/**
 * Подменить цену ноги тем, что человек видит на сайте.
 *
 * Контора — кэф, три знака вниз. Биржа — центы, кэф = 1/p. Стакан сводим к
 * одному уровню своей цены с прежней долларовой глубиной: иначе расчёт
 * продолжит идти по вчерашним ask и сумма не ответит на ввод.
 */
function applyOwnQuote(leg: ForkLeg, raw: string): ForkLeg | null {
  const kind = quoteKind(leg);
  const parsed = readOwnQuote(raw, kind);
  if (parsed === null) return null;
  if (kind === 'cents') {
    const price = new Decimal(parsed).div(100);
    if (price.lte(0) || price.gte(1)) return null;
    const odds = new Decimal(1).div(price).toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
    const share = price.toDecimalPlaces(PRICE_STEP, Decimal.ROUND_UP).toString();
    let book = leg.book;
    if (book && book.length > 0) {
      const depth = book.reduce(
        (sum, [p, q]) => sum.plus(new Decimal(p).times(q)),
        new Decimal(0),
      );
      const size = depth.gt(0) ? depth.div(price) : new Decimal(0);
      book = [[share, size.toString()]];
    }
    return { ...leg, odds, share, book };
  }
  const odds = new Decimal(parsed).toDecimalPlaces(3, Decimal.ROUND_DOWN);
  if (odds.lte(1)) return null;
  return { ...leg, odds: odds.toFixed(3) };
}

export const BLANK_CALC_ID = 'calc:blank';

/**
 * Стол без вилки: две ноги «руками», кэфы вбивает человек, суммы — из банка.
 * Валюта — та, в которой человек смотрит ленту: набил 30 в долларах — это 30 $.
 */
export function blankCalcFork(cur: 'RUB' | 'USD' = 'RUB'): Fork {
  const leg = (s: string, side: string, name: string): ForkLeg => ({
    s,
    side,
    odds: '2.000',
    url: null,
    participants: [],
    share: null,
    unit: 'money',
    cur,
    step: cur === 'USD' ? '0.01' : '1',
    name,
    p: 'section',
  });
  return { id: BLANK_CALC_ID, legs: [leg('hand:a', '1', 'первая'), leg('hand:b', '2', 'вторая')] };
}

function overlayHand(fork: Fork, hand?: HandLock): Fork {
  if (hand === undefined) return fork;
  const parsed = readOwnQuote(hand.odds, 'odds');
  if (parsed === null) return fork;
  const legs = fork.legs ?? [];
  if (legs.length !== 2) return fork;
  const first = legs[0]!;
  const odds = new Decimal(parsed).toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
  return {
    ...fork,
    legs: [
      ...legs,
      {
        s: HAND_SOURCE,
        side: 'X',
        odds,
        url: null,
        participants: first.participants,
        share: null,
        unit: 'money',
        cur: first.cur ?? 'RUB',
        step: first.step ?? '10',
        name: 'третья',
        p: 'section',
      },
    ],
  };
}

function overlayFork(fork: Fork, by?: Record<string, string>): Fork {
  if (by === undefined || Object.keys(by).length === 0) return fork;
  let changed = false;
  const legs = (fork.legs ?? []).map((leg) => {
    const raw = by[leg.s];
    if (raw === undefined) return leg;
    const next = applyOwnQuote(leg, raw);
    if (next === null) return leg;
    changed = true;
    return next;
  });
  return changed ? { ...fork, legs } : fork;
}

function stampOwn(
  rows: RowLeg[],
  live: readonly ForkLeg[],
  by?: Record<string, string>,
): RowLeg[] {
  if (by === undefined) return rows;
  return rows.map((row, i) => {
    const raw = by[row.source];
    const src = live[i];
    if (raw === undefined || src === undefined) return row;
    if (applyOwnQuote(src, raw) === null) return row;
    const liveCents = sharePrice(src)?.times(100).toNumber() ?? null;
    return { ...row, ownOdds: true, oddsLive: src.odds, centsLive: liveCents };
  });
}

function stampFee(rows: RowLeg[], fees?: FeeLock): RowLeg[] {
  if (fees === undefined) return rows;
  return rows.map((row) => {
    const pct = readOwnFee(fees.by[row.source] ?? '');
    if (pct === null) return row;
    return { ...row, ownFee: true, feePct: pct };
  });
}

function stampCur(rows: RowLeg[], curs?: CurLock): RowLeg[] {
  if (curs === undefined) return rows;
  return rows.map((row) => {
    const own = curs.by[row.source];
    return own === 'RUB' || own === 'USD' ? { ...row, ownCur: own } : row;
  });
}

/**
 * Курс валюты ноги к рублю банка: рублёвая касса — единица, долларовая — fx.
 * Округляют в валюте, которую видит контора, а не в рублях расчёта: круглые
 * 5 000 ₽ у JetTon превращались в 53.69 $ (Матвей, 15.09.2026).
 */
const legRate = (leg: ForkLeg, input: RowInput): Decimal =>
  rubForm(curOf(leg, input.curs), input.fx) ? new Decimal(1) : new Decimal(input.fx);

/** Шаг площадки в ЕЁ валюте (`betslip.Venue.stake_step`). Нет числа — десятка рублей или доллар. */
const venueStep = (leg: ForkLeg, input: RowInput): Decimal => {
  const n = num(leg.step);
  if (n !== null && n > 0) return new Decimal(n);
  return rubForm(curOf(leg, input.curs), input.fx) ? new Decimal(10) : new Decimal(1);
};

/**
 * Какую ногу делать круглой. Контора — первая не-биржа; нет такой — как
 * «грубее». Грубее — у кого шаг каталога больше, сравнение в рублях.
 */
const anchorIndex = (legs: readonly ForkLeg[], who: RoundWho, input: RowInput): number => {
  if (who === 'book') {
    const i = legs.findIndex((leg) => !isExchange(leg));
    if (i >= 0) return i;
  }
  let best = 0;
  let coarse = new Decimal(-1);
  legs.forEach((leg, i) => {
    const step = venueStep(leg, input).times(legRate(leg, input));
    if (step.gt(coarse)) {
      coarse = step;
      best = i;
    }
  });
  return best;
};

/** Лестница круглых сумм 1-2-5. «Чем ровнее — тем лучше»: идём сверху, пока сдвиг терпим. */
const NICE_LADDER = [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1] as const;
/** Якорь можно сдвинуть не больше чем на десятую часть: дальше это уже другая ставка. */
const ANCHOR_SHIFT_MAX = new Decimal('0.1');
/** Вторая контора: круглая сумма имеет право съесть не больше четверти плюса. */
const ROI_KEEP = new Decimal('0.75');
/** Набитая руками сумма: вторая нога почти точная, иначе исходы расходятся
 * (12 $ на полике → 1xBit 45 вместо 47.05, плюс +6.90 / +12.60 — 27.09). */
const ROI_KEEP_LOCK = new Decimal('0.97');

const toStep = (amount: Decimal, step: Decimal, dir: RoundDir): Decimal =>
  amount
    .div(step)
    .toDecimalPlaces(0, dir === 'near' ? Decimal.ROUND_HALF_UP : Decimal.ROUND_DOWN)
    .times(step);

/** Обе соседние круглые суммы: какая из них лучше — решает `accept`, не направление. */
const bothSteps = (amount: Decimal, step: Decimal): Decimal[] => {
  const low = toStep(amount, step, 'down');
  const high = low.plus(step);
  return low.eq(amount) ? [low] : [low, high];
};

/** Ставка, приведённая к шагу кассы в её валюте; меньше шага касса не примет. */
const venueStakeRub = (stakeRub: Decimal, leg: ForkLeg, input: RowInput): Decimal => {
  const rate = legRate(leg, input);
  const step = venueStep(leg, input);
  let amount = toStep(stakeRub.div(rate), step, 'down');
  if (amount.lte(0)) amount = step;
  return amount.times(rate);
};

/**
 * Круглая ставка конторы в её валюте, в рублях банка на выходе. Шаг человека —
 * порядок (десятки/сотни/тысячи) в валюте кассы; ниже по лестнице спускаемся,
 * пока `score` не согласится (число — годится, больше — лучше; null — нет).
 * `dir` задан — округляем в его сторону; `null` — смотрим обе соседние круглые
 * и берём лучшую по `score`. Кратность шагу кассы держится всегда: JetTon
 * принимает по 2 $, и 55 там не ставка.
 */
const niceStakeRub = (
  stakeRub: Decimal,
  leg: ForkLeg,
  input: RowInput,
  wanted: number,
  dir: RoundDir | null,
  score: (candidateRub: Decimal) => Decimal | null,
  lastResort: (candidateRub: Decimal) => Decimal | null = score,
): Decimal => {
  const rate = legRate(leg, input);
  const step = venueStep(leg, input);
  const amount = stakeRub.div(rate);
  const rungs = NICE_LADDER.map((rung) => new Decimal(rung)).filter(
    (nice) => nice.lte(wanted) && nice.gt(step) && nice.mod(step).isZero(),
  );
  // Последняя ступень — шаг самой кассы: круглее не вышло, но кратность держим.
  rungs.push(step);
  for (const nice of rungs) {
    const judge = nice.eq(step) ? lastResort : score;
    const candidates = dir === null ? bothSteps(amount, nice) : [toStep(amount, nice, dir)];
    let best: { rub: Decimal; score: Decimal } | null = null;
    for (const candidate of candidates) {
      if (candidate.lte(0)) continue;
      const rub = candidate.times(rate);
      const got = judge(rub);
      if (got !== null && (best === null || got.gt(best.score))) best = { rub, score: got };
    }
    if (best !== null) return best.rub;
  }
  return venueStakeRub(stakeRub, leg, input);
};

/** Контора, которую человек вводит сам: не биржа и не нога «против». */
const isMoneyBook = (leg: ForkLeg): boolean => !isExchange(leg) && leg.lay !== true;

/**
 * Остальные конторы после якоря: тоже круглые, пока плюс жив. Раньше вторую
 * ногу «подгоняли» до копейки — 1 001.11 у конторы читается как арбитраж, и
 * автоподстановка теряла смысл (Матвей, 15.09.2026). Биржа примет любую сумму.
 */
const roundOthers = (
  legs: readonly ForkLeg[],
  odds: readonly Decimal[],
  stakes: Decimal[],
  input: RowInput,
  anchor: number,
  wanted: number,
  dir: RoundDir,
  keep: Decimal = ROI_KEEP,
  limits: readonly (Decimal | null)[] = [],
): Decimal[] => {
  const out = [...stakes];
  const roiOf = (plan: readonly Decimal[]): { roi: Decimal; total: Decimal } => {
    const total = plan.reduce((sum, stake) => sum.plus(stake), new Decimal(0));
    const worst = plan.reduce(
      (min, stake, i) => Decimal.min(min, stake.times(odds[i]!)),
      new Decimal(Infinity),
    );
    return { roi: total.gt(0) ? worst.div(total).minus(1) : new Decimal(-1), total };
  };
  const bank = new Decimal(ceilingOf(input));
  legs.forEach((leg, i) => {
    if (i === anchor || !isMoneyBook(leg)) return;
    const base = roiOf(out);
    // Направление здесь решает плюс, а не настройка: «вниз» — обещание не
    // вылезти за банк, и оно проверяется суммой.
    const trial = (candidate: Decimal): { roi: Decimal; total: Decimal } | null => {
      const limit = limits[i];
      if (limit != null && candidate.gt(limit)) return null;
      const plan = [...out];
      plan[i] = candidate;
      const next = roiOf(plan);
      return dir === 'down' && next.total.gt(bank) ? null : next;
    };
    out[i] = niceStakeRub(
      out[i]!,
      leg,
      input,
      wanted,
      null,
      (candidate) => {
        const next = trial(candidate);
        if (next === null) return null;
        if (base.roi.lte(0)) return next.roi.gte(base.roi) ? next.roi : null;
        return next.roi.gt(0) && next.roi.gte(base.roi.times(keep)) ? next.roi : null;
      },
      // На шаге кассы круглее уже некуда: из двух соседних берём ту, где плюс больше.
      (candidate) => trial(candidate)?.roi ?? null,
    );
  });
  return out;
};

const refill = (calc: readonly Leg[], quote: VolumeQuote, stakes: readonly Decimal[]): VolumeQuote => {
  const fills = calc.map((leg, i) => fillLeg(leg, stakes[i]!));
  const total = fills.reduce((sum, fill) => sum.plus(fill.stake), new Decimal(0));
  return { ...quote, fills, total };
};

/**
 * После вершины объёма: округлить якорь в валюте его кассы, вторую ногу
 * набрать из выплаты и тоже сделать круглой, биржу подстроить по стакану.
 */
const rebalanceQuote = (
  legs: readonly ForkLeg[],
  calc: readonly Leg[],
  quote: VolumeQuote,
  input: RowInput,
  anchor: number,
  wanted: number,
  dir: RoundDir,
): VolumeQuote => {
  const odds = quote.fills.map((fill) => fill.odds);
  const raw = quote.fills.map((fill) => fill.stake);
  const limits = limitsOf(calc);
  const anchorRaw = raw[anchor]!;
  const anchorCap = limits[anchor] ?? null;
  const anchorRub = niceStakeRub(anchorRaw, legs[anchor]!, input, wanted, dir, (candidate) => {
    if (anchorCap !== null && candidate.gt(anchorCap)) return null;
    const shift = candidate.minus(anchorRaw).abs();
    return shift.lte(anchorRaw.times(ANCHOR_SHIFT_MAX)) ? shift.neg() : null;
  });
  const target = anchorRub.times(odds[anchor]!);
  const plan = odds.map((o, i) =>
    i === anchor ? anchorRub : target.div(o).toDecimalPlaces(2, Decimal.ROUND_HALF_UP),
  );
  const keep = input.hold === true ? ROI_KEEP_LOCK : ROI_KEEP;
  return refill(calc, quote, roundOthers(legs, odds, plan, input, anchor, wanted, dir, keep, limits));
};

/** Потолок каждой ноги в рублях расчёта: округление не выводит ставку за баланс. */
const limitsOf = (calc: readonly Leg[]): (Decimal | null)[] =>
  calc.map((leg) => (leg.limit == null ? null : new Decimal(leg.limit)));

/** Набитое плечо: остальные конторы вокруг него — круглые, биржа по стакану. */
const roundAroundLock = (
  legs: readonly ForkLeg[],
  calc: readonly Leg[],
  quote: VolumeQuote,
  input: RowInput,
  lockAt: number,
  wanted: number,
  dir: RoundDir,
): VolumeQuote => {
  // Плюс биржи — после комиссии: по голому кэфу перекос казался выгодой.
  const odds = quote.fills.map((fill) => effectiveOdds(fill.odds, fill.costFraction));
  const plan = quote.fills.map((fill) => fill.stake);
  return refill(
    calc,
    quote,
    roundOthers(legs, odds, plan, input, lockAt, wanted, dir, ROI_KEEP_LOCK, limitsOf(calc)),
  );
};

/**
 * Числа ног из котировки. `fit` — якорь шагом человека, вторая из выплаты;
 * иначе каждая контора режется своим шагом площадки (как лента до студии).
 */
function selOf(leg: ForkLeg): string | null {
  if (typeof leg.sel === 'string' && leg.sel.includes('~')) return leg.sel;
  const eid = typeof leg.eid === 'string' ? leg.eid.trim() : '';
  // Positive: страницы матча нет, обещание несёт data-id тем же sel,
  // который act.ts уже кладёт в расширение. Движок, CHA и CHV — один формат.
  if (leg.s === 'csgopositive' && /^\d+$/.test(eid)) return `${eid}~positive~`;
  return null;
}

function coordsOf(
  leg: ForkLeg,
  marketKey?: string,
): { period: string | null; kind: string | null; line: string | null } {
  const fromLeg = typeof leg.period === 'string' && leg.period !== '' ? leg.period : null;
  const kind = marketPrefix(marketKey);
  const parsed = marketParts(marketKey);
  return {
    period: fromLeg ?? marketPeriod(marketKey),
    kind: kind !== '' ? kind : null,
    line: parsed.line !== null ? String(parsed.line) : null,
  };
}

/** The fork market's H/A anchor uses the fork participant order, while a
 * bookmaker leg may use the reverse order. Translate the anchor, not the
 * number: coordinates() derives the selected leg's signed line afterwards.
 * Unknown aliases must not silently become a bet on the opposite handicap. */
export function executionMarketKey(marketKey: string | undefined, canonicalWho: unknown, legWho: readonly string[]): string | undefined {
  // A team total names its team by the same H/A anchor, in the subject slot.
  if (marketKey === undefined) return marketKey;
  const teamTotal = marketKey.startsWith('ITTL:');
  if (!marketKey.startsWith('HCP:') && !teamTotal) return marketKey;
  if (!Array.isArray(canonicalWho) || canonicalWho.length !== 2 || legWho.length !== 2
    || canonicalWho.some((v) => typeof v !== 'string')) return undefined;
  const norm = (value: string) => participantText(value).normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();
  const canonical = canonicalWho.map((v) => norm(v as string));
  const local = legWho.map(norm);
  if (!canonical[0] || !canonical[1] || canonical[0] === canonical[1] || !local[0] || local[0] === local[1]) return undefined;
  if (canonical[0] === local[0] && canonical[1] === local[1]) return marketKey;
  if (canonical[0] !== local[1] || canonical[1] !== local[0]) return undefined;
  const parts = marketKey.split(':');
  const slot = teamTotal ? 5 : 3;
  if (parts.length !== 6 || !['H', 'A'].includes(parts[slot]!)) return undefined;
  parts[slot] = parts[slot] === 'H' ? 'A' : 'H';
  return parts.join(':');
}

function paintLegs(
  legs: readonly ForkLeg[],
  quote: VolumeQuote,
  input: RowInput,
  fit: boolean,
  marketKey?: string,
  sport?: string,
  eventLine?: string,
  eventStart?: string,
  marketParticipants?: unknown,
  forkId?: string,
): RowLeg[] {
  const sole = soleInitiator(legs);
  return legs.map((leg, i) => {
    const executionMarket = executionMarketKey(marketKey, marketParticipants, leg.participants ?? []);
    const fill = quote.fills[i]!;
    const isBook = Boolean(leg.book && leg.book.length > 0);

    // Цена внутри расчёта уже долларовая — курс сидел в объёме. В ордер идёт
    // худший забираемый уровень, а не средняя: по средней цене заявка не
    // исполнится, ею платят.
    const price = fill.limitPrice ?? fill.avgPrice;

    // Расход считается от ОКРУГЛЁННЫХ цены и штук, а не от VWAP: человек введёт
    // именно эти два числа, и списано будет их произведение.
    //
    // `fit` — конторы уже круглые в своей валюте (`rebalanceQuote`), число
    // берём как есть. Иначе каждая касса режется своим шагом в своей валюте.
    const lay = leg.lay === true && !isBook;
    const cut = fit ? fill.stake : venueStakeRub(fill.stake, leg, input);
    const feePct = feePctOf(leg, input.fees);
    const oddsNet = feePct === null ? leg.odds : commissionOdds(leg.odds, feePct).toString();
    const ticket = isBook || lay ? null : { stake: cut, payout: bookPayout(cut, oddsNet, input) };
    // Нога «против»: выделенный объём — это риск, а округляется ставка
    // соперника, потому что в форму биржи вводят именно её.
    const against = lay ? layTicket(fill.stake, leg.odds, leg.step ?? '10') : null;

    // Биржевая нога БЕЗ нашего стакана — это зеркало: глубины донор не отдаёт
    // вовсе, а цена шара приходит полем `share`. Раскладку это не трогает —
    // сколько уйдёт со счёта, по-прежнему считает конторская ветка по кэфу.
    // Переводится в биржевые единицы только то, что человек ВВОДИТ в форму
    // площадки: цена и количество. Иначе на Polymarket ему предлагали искать
    // поле «кэф», которого там нет (09.08, владелец).
    const quoted = !lay && input.fx > 0 ? sharePrice(leg) : null;
    const exchange = isBook || quoted !== null;
    const tick = exchangeTick(leg, isBook, exchange);
    // Зеркало часто кладёт POLY-стакан 50¢ при кэфе ленты 1.951 (51.3¢).
    // В форму и на строку идёт цена ленты, вниз к тику — иначе купон 50¢,
    // а лента обещает другие центы и другие шэры.
    const bookPx = isBook && price !== null ? new Decimal(price) : null;
    const tapePx = bookPx !== null && !shareFieldStale(leg) ? bookPx : (quoted ?? bookPx);
    const snapped =
      tapePx !== null && tick !== null ? (snapToTick(tapePx, tick) ?? tapePx) : tapePx;

    const cents = snapped !== null ? snapped.times(100).toNumber() : null;
    const usdRoom =
      input.fx > 0
        ? isBook
          ? new Decimal(fill.stake).div(input.fx)
          : ticket !== null
            ? ticket.stake.div(input.fx)
            : null
        : null;
    const shares =
      isBook && !shareFieldStale(leg) && fill.shares !== null
        ? sharesShown(fill.shares.div(input.fx)).toNumber()
        : snapped !== null && usdRoom !== null && snapped.gt(0)
          ? sharesShown(usdRoom.div(snapped)).toNumber()
          : null;

    const cur = curOf(leg, input.curs);
    // У биржи в форму идут центы и шеры, не эта сумма. Считать её здесь нельзя:
    // `ticket` у книги пуст, и обращение к нему уронило бы живую строку.
    const typed = exchange ? 0 : (lay ? against!.stake : ticket!.stake).toNumber();
    const bookUsd = exchange && cents !== null && shares !== null ? new Decimal(cents).div(100).times(shares).toNumber() : null;
    const moneyUsd = !exchange && !isRubCur(cur) && input.fx > 0 ? new Decimal(typed).div(input.fx).toNumber() : null;
    const usd = bookUsd ?? moneyUsd;
    const rub = isBook
      ? new Decimal(usd ?? 0).times(input.fx).toNumber()
      : lay
        ? against!.risk.toNumber()
        : ticket!.stake.toNumber();

    // Возврат биржевой ноги — шеры, оставшиеся ПОСЛЕ комиссии: она удерживается
    // штуками, и на счёт приходит меньше, чем куплено.
    const back = isBook
      ? shares !== null && snapped !== null
        ? sharesAfterFee(String(shares), snapped,
            feePct !== null ? new Decimal(feePct).div(100) : leg.feeRate,
            leg.feeExponent ?? 1, leg.feeModel).times(input.fx).toNumber()
        : 0
      : lay
        ? against!.payout.toNumber()
        : ticket!.payout.toNumber();

    return {
      payoutModel: isBook
        ? { kind: 'shares', rate: feePct !== null ? new Decimal(feePct).div(100).toString() : (leg.feeRate ?? SPORTS_FEE_RATE),
            exponent: leg.feeExponent ?? 1, assumed: feePct === null && leg.feeRate === undefined }
        : !exchange || feePct !== null
          ? { kind: 'profit', rate: new Decimal(feePct ?? '0').div(100).toString() }
          : undefined,
      source: leg.s,
      name: participantText(leg.name ?? (leg.side === 'D' ? 'Ничья' : (leg.participants ?? [])[sideIndex(leg)]) ?? leg.s),
      who: (leg.participants ?? []).map(participantText),
      side: participantText(leg.side ?? ''),
      // Единица — не то, что сказал сервер, а то, что мы сумели посчитать. Нога
      // с `unit: share` и непосчитанной ценой показала бы «0 ¢ × 0.0», а это не
      // «мы не знаем», это ноль: конторский вид по кэфу тут честнее.
      unit: exchange ? 'share' : 'money',
      cur,
      headline: exchange
        ? `${centsText(cents ?? 0)} ¢`
        : moneyUsd !== null
          ? `${moneyUsd.toFixed(2)} $`
          : `${Math.round(typed)} ₽`,
      detail: exchange ? `× ${(shares ?? 0).toFixed(1)} шер` : `кэф ${leg.odds}`,
      rub,
      payout: back,
      usd,
      ...(isBook && usdRoom !== null
        ? { marketUsd: usdRoom.toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber(), book: leg.book! }
        : {}),
      shares,
      cents,
      odds: leg.odds,
      lay,
      layStake: lay ? against!.stake.toNumber() : null,
      cap: capOf(leg, input),
      ...(isBook && shares !== null && thinBook(leg, shares) ? { thin: true } : {}),
      // Токен едет дальше только у ноги с НАШИМ стаканом: у зеркала цена шара
      // есть, а книги и токена нет, и ордер по ней собрать не из чего.
      asset: isBook || (forkId !== undefined && isPreparedFeed2Id(forkId) && Boolean(leg.asset))
        ? (leg.asset ?? null)
        : null,
      tick,
      minSize: isBook ? num(leg.minSize) : null,
      precision: leg.p ?? 'section',
      url: leg.betUrl ?? leg.url,
      sel: selOf(leg),
      ...(typeof leg.eid === 'string' && /^\d+$/.test(leg.eid.trim()) ? { eid: leg.eid.trim() } : {}),
      ...coordsOf(leg, marketKey),
      ...(executionMarket ? { marketKey: executionMarket } : {}),
      ...(sport ? { sport } : {}),
      ...(eventLine === 'live' || eventLine === 'prematch' ? { eventLine } : {}),
      ...(eventStart && Number.isFinite(Date.parse(eventStart)) ? { eventStart: Date.parse(eventStart) } : {}),
      // Значок «вилки» — у одной ноги: зеркало, пометившее обе, инициатора не назвало.
      initiator: leg.initiator === true ? sole === i : leg.initiator,
      initiatorCount: leg.initiatorCount,
    };
  });
}

function stampFree(rows: RowLeg[], free?: FreeLock, fees?: FeeLock): RowLeg[] {
  if (free === undefined) return rows;
  return rows.map((row) => {
    if (row.source !== free.source) return row;
    const win = new Decimal(row.rub).times(Decimal.max(0, new Decimal(row.odds).minus(1)));
    const pct = readOwnFee(fees?.by[row.source] ?? '');
    const cut =
      pct === null ? win : win.times(new Decimal(1).minus(new Decimal(pct).div(100)));
    return { ...row, freebet: true, payout: cut.toNumber() };
  });
}

function bookPayout(stake: Parameters<typeof stakePayout>[0], odds: Parameters<typeof stakePayout>[1], input: RowInput): Decimal {
  return (input.feedKind ?? FEED_KIND) === 'cupis'
    ? new Decimal(stake).times(odds).toDecimalPlaces(2, Decimal.ROUND_DOWN)
    : stakePayout(stake, odds);
}

function shownMoney(rows: readonly RowLeg[]): { staked: number; payout: number; profit: number } {
  const cash = rows.filter((leg) => !leg.freebet);
  const staked = cash.reduce((sum, leg) => sum.plus(leg.rub), new Decimal(0));
  const payout = Decimal.min(...rows.map((leg) => new Decimal(leg.payout)));
  return { staked: staked.toNumber(), payout: payout.toNumber(), profit: payout.minus(staked).toNumber() };
}

function readForm(form: string): Decimal | null {
  const raw = form.trim().replace(/\s/g, '').replace(',', '.');
  if (raw === '' || raw === '.') return null;
  try {
    const parsed = new Decimal(raw);
    return parsed.isFinite() && parsed.gt(0) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Набитое — в рублях банка. На бирже замок читает либо баксы расхода, либо
 * шеры купона: `as === 'share'` держит штуки, центы скачут — расход едет.
 * Иначе баксы стоят, шеры едут за ценой. `quoteFromAnchor` знает только расход.
 */
function lockBudget(leg: ForkLeg, form: string, input: RowInput): Decimal | null {
  const raw = readForm(form);
  if (raw === null) return null;
  const exchange = isExchange(leg);
  if (exchange) {
    if (raw.lte(0) || !(input.fx > 0)) return null;
    if (input.lock?.as === 'share') {
      const price = sharePrice(leg);
      if (price === null || price.lte(0)) return null;
      return raw.times(price).times(input.fx);
    }
    return raw.times(input.fx);
  }
  const step = leg.step ?? '10';
  if (leg.lay === true) {
    const ticket = bookmakerTicket(raw, '2', step);
    const opp = rubForm(curOf(leg, input.curs), input.fx) ? ticket.stake : ticket.stake.times(input.fx);
    const odd = new Decimal(leg.odds);
    if (odd.lte(1)) return null;
    return opp.times(odd.minus(1));
  }
  const ticket = bookmakerTicket(raw, leg.odds, step);
  return rubForm(curOf(leg, input.curs), input.fx) ? ticket.stake : ticket.stake.times(input.fx);
}

/** Чтобы на экране стояло то же число, что человек набил, а не хвост деления. */
function paintLocked(
  rows: RowLeg[],
  index: number,
  proto: ForkLeg,
  form: string,
  input: RowInput,
): RowLeg[] {
  const raw = readForm(form);
  if (raw === null) return rows;
  const leg = rows[index];
  if (leg === undefined) return rows;
  const next = { ...leg };
  const feePct = feePctOf(proto, input.fees);
  const rate = feePct !== null ? new Decimal(feePct).div(100).toString() : (proto.feeRate ?? SPORTS_FEE_RATE);
  const oddsNet = feePct === null ? proto.odds : commissionOdds(proto.odds, feePct).toString();
  if (leg.unit === 'share') {
    next.payoutModel = { kind: 'shares', rate, exponent: proto.feeExponent ?? 1,
      assumed: feePct === null && proto.feeRate === undefined };
    const price = (leg.cents ?? 0) / 100;
    if (input.lock?.as === 'share') {
      next.shares = raw.toNumber();
      next.usd = price > 0 ? next.shares * price : 0;
    } else {
      const usdAmt = raw.toNumber();
      next.usd = usdAmt;
      next.shares = price > 0 ? sharesShown(String(usdAmt / price)).toNumber() : 0;
    }
    next.detail = `× ${next.shares.toFixed(1)} шер`;
    next.rub = (next.usd ?? 0) * input.fx;
    if (price > 0) {
      next.payout = sharesAfterFee(String(next.shares), String(price), rate, proto.feeExponent ?? 1, proto.feeModel).times(input.fx).toNumber();
    }
  } else if (leg.lay) {
    const ticket = bookmakerTicket(raw, '2', proto.step ?? '10');
    const opp = ticket.stake.toNumber();
    const odd = num(leg.odds) ?? 0;
    const oppRub = rubForm(leg.cur, input.fx) ? opp : opp * input.fx;
    next.layStake = oppRub;
    next.rub = oppRub * Math.max(0, odd - 1);
    const against = layTicket(String(next.rub), leg.odds, proto.step ?? '10');
    next.payout = against.payout.toNumber();
    next.headline = rubForm(leg.cur, input.fx) ? `${Math.round(opp)} ₽` : `${opp.toFixed(2)} $`;
  } else {
    const ticket = bookmakerTicket(raw, oddsNet, proto.step ?? '10');
    const amount = ticket.stake.toNumber();
    if (rubForm(leg.cur, input.fx)) {
      next.rub = amount;
      next.usd = null;
      next.payout = bookPayout(ticket.stake, oddsNet, input).toNumber();
      next.headline = `${Math.round(amount)} ₽`;
    } else {
      next.usd = amount;
      next.rub = amount * input.fx;
      next.payout = bookPayout(String(next.rub), oddsNet, input).toNumber();
      next.headline = `${amount.toFixed(2)} $`;
    }
  }
  const out = [...rows];
  out[index] = next;
  return out;
}

/** Максимум ставки на БК, который покрывает встречная нога.
 * В отличие от ручного замка, остаток не отменяет лимит и глубину стакана. */
function quoteTransferBalance(
  legs: readonly ForkLeg[], calc: readonly Leg[], input: RowInput,
): { quote: VolumeQuote; cappedBy: Row['cappedBy'] } | null {
  const budget = input.transferBudget;
  if (!budget || legs.length !== 2 || !(input.fx > 0)) return null;
  const pmAt = legs.findIndex((leg) => leg.s === 'polymarket');
  if (pmAt < 0) return null;
  const at = 1 - pmAt;
  const book = calc[at]!;
  const pm = calc[pmAt]!;
  if (book.kind !== 'fixed' || !isMoneyBook(legs[at]!) || new Decimal(book.odds).lte(0)) return null;
  let stake = new Decimal(budget.amount).times(budget.currency === 'USD' ? input.fx : 1);
  if (!stake.isFinite() || stake.lte(0)) return null;
  let cappedBy: Row['cappedBy'] = 'баланс БК';
  if (book.limit != null && stake.gt(book.limit)) {
    stake = new Decimal(book.limit);
    const tightKind = legCap(legs[at]!, input)?.kind;
    cappedBy = tightKind === 'balance' ? 'баланс' : tightKind === 'freebet' ? 'фрибет' : 'лимит';
  }
  if (pm.kind === 'book') {
    const depth = pm.asks.reduce((sum, [price, size]) => sum.plus(new Decimal(price).times(size)), new Decimal(0));
    const fill = fillLeg(pm, depth);
    const covered = fill.stake.times(fill.odds).times(new Decimal(1).minus(fill.costFraction)).div(book.odds);
    if (stake.gt(covered)) {
      stake = covered;
      cappedBy = legCap(legs[pmAt]!, input)?.kind === 'balance' ? 'баланс' : 'стакан';
    }
  } else if (pm.limit != null) {
    const covered = new Decimal(pm.limit).times(pm.odds).div(book.odds);
    if (stake.gt(covered)) { stake = covered; cappedBy = 'допущение'; }
  }
  // Баланс PM из студии / поля «на PM»: режет якорь БК, а не только глубину стакана.
  const pmCap = pm.limit != null
    ? new Decimal(pm.limit)
    : (() => {
      const raw = input.caps.polymarket;
      if (raw == null || !Number.isFinite(raw) || raw <= 0) return null;
      const cur = input.capsCur?.polymarket ?? 'USD';
      return new Decimal(raw).times(cur === 'USD' ? input.fx : 1);
    })();
  if (pmCap !== null && pmCap.gt(0)) {
    try {
      const trial = quoteFromAnchor(calc, at, stake);
      const spent = trial.fills[pmAt]?.stake;
      if (spent && spent.gt(pmCap)) {
        stake = stake.times(pmCap.div(spent));
        cappedBy = 'баланс';
      }
    } catch {
      // якорь без встречной — ниже отрежет шаг
    }
  }
  // Округляем только вниз в валюте кассы; остаток меньше шага не увеличиваем.
  const rate = legRate(legs[at]!, input);
  const step = venueStep(legs[at]!, input);
  stake = toStep(stake.div(rate), step, 'down').times(rate);
  if (stake.lte(0)) return null;
  return { quote: quoteFromAnchor(calc, at, stake), cappedBy };
}

/**
 * Посчитать строку. `null` — посчитать нечем: нет курса, нет стакана, нет цен.
 *
 * Возвращать здесь нули нельзя. Строка с нулевой прибылью выглядит как строка,
 * которую посчитали и признали бесполезной, а это другое утверждение.
 */
export function buildRow(fork: Fork, input: RowInput): Row | null {
  if (isCupisForkId(fork.id)) input = { ...input, feedKind: 'cupis' };
  const live = healShareFork(alignFork(fork));
  const ownBy = lockOf(input.odds, live.id)?.by;
  const fees = lockOf(input.fees, live.id);
  const curs = lockOf(input.curs, live.id);
  const free = bookFreeOf(live.id, live.legs ?? [], input);
  const hand = lockOf(input.hand, live.id);
  const scoped: RowInput = { ...input, fees, curs, free, hand };
  fork = healShareFork(overlayHand(overlayFork(live, ownBy), hand));
  const feeApplied = fees !== undefined && Object.keys(fees.by).some((s) => readOwnFee(fees.by[s] ?? '') !== null);
  const ownApplied = fork !== live || feeApplied || free !== undefined;
  const legs = fork.legs ?? [];
  const marketParticipants = fork.participants ?? (fork.mir ? [] : legs[0]?.participants);
  // Вилка без двух названных касс — не вилка. Числа без второй площадки
  // выглядят как готовая ставка, и человек берёт одну ногу на весь банк.
  // Пустой `s` — дыра в кадре; одной ноги тоже мало. Демошный `hidden`
  // проходит: это маска, слово на экране есть.
  if (legs.length < 2 || legs.some((leg) => !namedSource(leg.s))) return null;
  const calc = legs.map((leg) => calcLeg(leg, scoped, fees));
  if (calc.some((leg) => leg === null)) return null;

  const stepWanted = input.roundStep ?? 10;
  const dir: RoundDir = input.roundDir === 'near' ? 'near' : 'down';
  const who: RoundWho = input.roundWho === 'coarse' ? 'coarse' : 'book';
  const anchor = anchorIndex(legs, who, scoped);

  const lock = input.lock?.forkId === live.id ? input.lock : undefined;
  const lockAt = lock === undefined ? -1 : legs.findIndex((leg) => leg.s === lock.source);
  const lockedStake = lockAt >= 0 && lock !== undefined ? lockBudget(legs[lockAt]!, lock.form, scoped) : null;

  let peak: VolumeQuote;
  let peakTotal: Decimal;
  let quote: VolumeQuote;
  let rows: RowLeg[];
  let staked: number;
  let payout: number;
  let profit: number;
  // A CUPIS transfer spends the chosen bank; maximizing a negative profit
  // would shrink it to the minimum stake and distort its displayed ROI.
  const cupisTransfer = (input.feedKind ?? FEED_KIND) === 'cupis' && Number(fork.roi) <= 0;
  const ruFeed = (input.feedKind ?? FEED_KIND) === 'ru';
  const transferBudget = (input.feedKind ?? FEED_KIND) === 'transfers' ? input.transferBudget : undefined;
  let transferCap: Row['cappedBy'] = null;

  if (lockedStake !== null && lockAt >= 0 && lock !== undefined) {
    try {
      quote = quoteFromAnchor(calc as Leg[], lockAt, lockedStake);
    } catch {
      return null;
    }
    if (quote.total.lte(0)) return null;
    peak = quote;
    peakTotal = quote.total;
    try {
      const next = roundAroundLock(legs, calc as Leg[], quote, scoped, lockAt, stepWanted, dir);
      if (next.total.gt(0)) quote = next;
    } catch {
      // Круглая вторая нога — удобство, не условие строки.
    }
    rows = paintLocked(
      paintLegs(legs, quote, scoped, true, fork.market, fork.sport, fork.line, fork.ko, marketParticipants, fork.id),
      lockAt,
      legs[lockAt]!,
      lock.form,
      scoped,
    );
    rows = stampFree(rows, free, fees);
    ({ staked, payout, profit } = shownMoney(rows));
    // Набитое плечо — человек уже в ставке. Нулевой плюс не прячет карточку.
    if (staked <= 0) return null;
  } else if (transferBudget !== undefined) {
    try {
      const sized = quoteTransferBalance(legs, calc as Leg[], scoped);
      if (sized === null) return null;
      peak = quote = sized.quote;
      peakTotal = peak.total;
      transferCap = sized.cappedBy;
      rows = stampFree(paintLegs(legs, quote, scoped, true, fork.market, fork.sport, fork.line, fork.ko, marketParticipants, fork.id), free, fees);
      ({ staked, payout, profit } = shownMoney(rows));
      if (staked <= 0) return null;
    } catch {
      return null;
    }
  } else {
    try {
      // Свой кэф — ответ «сколько ставить при ЭТИХ числах», а не поиск
      // вершины прибыли. Если плюса нет, bestVolume сжимает банк до шага
      // кассы: минус на 10 ₽ меньше, чем на десяти тысячах, и карточка
      // предлагает вбить десятку. Человек вбил кэф с сайта, чтобы увидеть
      // раскладку банка, даже когда вилка уже в минусе.
      peak = ownApplied || cupisTransfer || (input.feedKind ?? FEED_KIND) === 'transfers'
        ? quoteAtVolume(ceilingOf(input), calc as Leg[])
        : bestVolume(calc as Leg[], ceilingOf(input));
      // Negative RU rows use the chosen bank: minimizing a loss down to one
      // stake step distorts ROI after rounding (e.g. -3% becomes -5%).
      if ((input.hold === true || ruFeed) && !ownApplied && !peak.profit.gt(0)) {
        peak = quoteAtVolume(ceilingOf(input), calc as Leg[]);
      }
      if (peak.total.lte(0)) return null;
      // Потолок «банк» смотрит объём ДО шага человека: после округления
      // недобор в десятки на банке в пятьсот уже два процента, и строка,
      // взявшая банк целиком, сказала бы, что её ничто не ограничивало.
      peakTotal = peak.total;
    } catch {
      return null;
    }

    quote = peak;
    try {
      const next = rebalanceQuote(legs, calc as Leg[], peak, scoped, anchor, stepWanted, dir);
      if (next.total.gt(0)) quote = next;
    } catch {
      // Шаг человека не имеет права прятать вилку: ниже останется вершина.
    }

    rows = stampFree(paintLegs(legs, quote, scoped, true, fork.market, fork.sport, fork.line, fork.ko, marketParticipants, fork.id), free, fees);
    // Итог считается от чисел строки, а не от расчёта до округления. Иначе экран
    // обещает прибыль, которой не будет: человек введёт округлённые числа, и
    // разница между обещанием и кассой станет его претензией к сканеру.
    ({ staked, payout, profit } = shownMoney(rows));
    // Шаг человека не прячет вилку, у которой плюс ещё жив на шаге площадки:
    // ребаланс по сырому кэфу не знает комиссии стакана и на тонкой книге
    // съедает гарантию, хотя вершина объёма её ещё держит.
    if (staked <= 0 || (profit <= 0 && !cupisTransfer && (input.feedKind ?? FEED_KIND) !== 'transfers')) {
      rows = stampFree(paintLegs(legs, peak, scoped, false, fork.market, fork.sport, fork.line, fork.ko, marketParticipants, fork.id), free, fees);
      quote = peak;
      ({ staked, payout, profit } = shownMoney(rows));
    }
    // Без ставки строки нет: считать нечем. Минус и ноль — ответ, не дыра:
    // сервер уже отдаёт вилки от −5 %, и прятать их здесь значило бы, что
    // человек ставит пол в сите, а лента всё равно пустая.
    if (staked <= 0) return null;
  }
  rows = stampDead(stampCur(stampFee(stampOwn(rows, live.legs ?? [], ownBy), fees), curs));
  const blockers = blockersOf(live, input);
  const gaps = gapsOf(live, rows);

  return {
    ...(transferBudget !== undefined ? { transferBudget } : {}),
    fork: live,
    legs: rows,
    profit,
    staked,
    payout,
    roiPct: new Decimal(profit).div(staked).times(100).toNumber(),
    netRoiExact: new Decimal(profit).div(staked).times(100).toString(),
    ...(transferCap !== null
      ? { cappedBy: transferCap }
      : cappedByOf(calc as Leg[], { ...peak, total: peakTotal }, legs, rows, input)),
    lead: fork.lead === true && blockers.length === 0 && gaps.length === 0,
    blockers,
    doubt: doubtOf(fork, input),
    gaps,
  };
}

/**
 * Правила гейта, после которых вилка скорее мертва, чем жива: все они говорят
 * не «мы чего-то не знаем», а «эта цена неверна». Набор повторяет серверные
 * классы (`backend/forq/engine/ruleclass.py`): арифметика и рыночные свидетели,
 * без правил «нашей незрелости» (нет ссылки, не домерили конвенцию) и без
 * каденции трубы (`stale`/`frozen`/`unwitnessed`). У Фонбета 95-й перцентиль
 * возраста ноги ~51 с при пороге `stale` 15 с — красить это смертью значит
 * орать на рабочую пару, а не на мёртвый рынок.
 */
const DEAD_RULES = new Set([
  'odds_anomaly',
  'palp',
  'no_consensus',
  'crowd_outlier',
  'impossible_roi',
  'suspended',
  'one_sided',
  'rules_mismatch',
  'expired',
  'statue',
  'draw_possible',
]);

function doubtOf(fork: Fork, input: RowInput): Row['doubt'] {
  const codes = (fork.rl ?? []).map((rule) => rule.c);
  if (codes.some((code) => DEAD_RULES.has(code))) return 'dead';
  if (codes.includes('too_short') || codes.includes('flutter') || tooShort(fork, input.tempo)) {
    return 'fast';
  }
  return null;
}

/**
 * Цена смерти биржевой ноги: по выплате второй ноги ПОСЛЕ всех штампов —
 * фрибет и комиссия конторы меняют её кэф, и цена смерти вместе с ним.
 */
function stampDead(rows: RowLeg[]): RowLeg[] {
  if (rows.length !== 2) return rows;
  return rows.map((leg, i) => {
    const other = rows[1 - i]!;
    if (leg.unit !== 'share' || !leg.book?.length || !(other.rub > 0) || !(other.payout > 0)) return leg;
    const model = leg.payoutModel?.kind === 'shares' ? leg.payoutModel : null;
    const dead = deadPrice(
      new Decimal(other.payout).div(other.rub),
      model === null ? {} : { rate: model.rate, exponent: model.exponent },
    );
    return dead === null ? leg : { ...leg, marketDead: dead.toString() };
  });
}

/** Какой из участников — наша сторона. Нужен, когда сервер не назвал имя. */
const sideIndex = (leg: ForkLeg): number => (leg.side === 'A' ? 1 : 0);

/** По цене вилки в стакане меньше шаров, чем в плане: остаток нальётся хуже или не нальётся. */
function thinBook(leg: ForkLeg, shares: number): boolean {
  const odds = new Decimal(leg.odds);
  if (!odds.gt(1) || !leg.book || leg.book.length === 0) return false;
  const edge = new Decimal(1).div(odds).toDecimalPlaces(4, Decimal.ROUND_HALF_UP);
  const depth = leg.book.reduce(
    (acc, [px, size]) => (new Decimal(px).lte(edge) ? acc.plus(size) : acc),
    new Decimal(0),
  );
  return depth.lt(String(shares));
}

function capOf(leg: ForkLeg, input: RowInput): RowLeg['cap'] {
  const declared = leg.cap;
  // Глубина стакана — своё число («в стакане»), баланс кошелька его не подменяет.
  if (declared && declared.kind === 'book') return { kind: 'book', value: num(declared.value) };
  const tight = legCap(leg, input);
  if (tight !== null) return { kind: tight.kind, value: tight.value, cur: tight.cur };
  if (declared && declared.kind !== 'assumed') {
    return { kind: declared.kind, value: num(declared.value) };
  }
  return { kind: 'assumed', value: null };
}

/**
 * Во что упёрся объём. `null` — ни во что: дальше просто невыгодно.
 *
 * Ищется не превышение, а КАСАНИЕ. На оптимуме ограничение ровно достигнуто и
 * ни разу не нарушено, поэтому признак «эту ногу пришлось урезать» здесь всегда
 * пуст: урезать было нечего, вершина сама встала на границу.
 *
 * Виды потолка названы разными словами намеренно. «Стакан» и «лимит» — чужие
 * факты, «банк» — решение оператора, «допущение» — его же догадка. Сложив их в
 * одно слово, мы отправили бы человека поднимать лимит, которого никто не
 * ставил, или доверять числу, которое он сам и придумал.
 */
function cappedByOf(
  calc: readonly Leg[],
  quote: VolumeQuote,
  legs: readonly ForkLeg[],
  rows: readonly RowLeg[],
  input: RowInput,
): Pick<Row, 'cappedBy' | 'capBalance'> {
  const touches = (value: Decimal, edge: Decimal): boolean => value.gte(edge.times('0.995'));
  const moneyAt = (
    i: number,
    by: 'баланс' | 'фрибет',
  ): Pick<Row, 'cappedBy' | 'capBalance'> => {
    const tight = legCap(legs[i]!, input);
    return tight === null
      ? { cappedBy: by }
      : { cappedBy: by, capBalance: { source: legs[i]!.s, value: tight.value, cur: tight.cur } };
  };

  // Сравнивается объём ДО округления. Округлённый упирается в банк с недобором
  // до десяти рублей, и на банке в пятьсот это уже два процента: строка,
  // выбравшая банк целиком, объявила бы, что её ничто не ограничивало.
  if (touches(quote.total, new Decimal(input.bank))) return { cappedBy: 'банк' };

  for (let i = 0; i < calc.length; i += 1) {
    const leg = calc[i]!;
    const stake = quote.fills[i]!.stake;
    if (leg.kind === 'fixed') {
      if (leg.limit != null && touches(stake, new Decimal(leg.limit))) {
        const kind = rows[i]!.cap.kind;
        if (kind === 'balance') return moneyAt(i, 'баланс');
        if (kind === 'freebet') return moneyAt(i, 'фрибет');
        return { cappedBy: kind === 'assumed' ? 'допущение' : 'лимит' };
      }
      continue;
    }
    if (leg.limit != null && touches(stake, new Decimal(leg.limit))) {
      return moneyAt(i, rows[i]!.cap.kind === 'freebet' ? 'фрибет' : 'баланс');
    }
    const depth = leg.asks.reduce(
      (sum, [price, size]) => sum.plus(new Decimal(price).times(size)),
      new Decimal(0),
    );
    if (depth.gt(0) && touches(stake, depth)) return { cappedBy: 'стакан' };
  }
  return { cappedBy: null };
}

/**
 * Чего не хватает строке, чтобы вести (Правило 1).
 *
 * Список слов, а не флаг: «строка не может вести» без причины — это отказ,
 * который нечем исправить. Увидев «лимит фонбета не назван», оператор вводит
 * своё число и видит, как строка поднимается в ведущие. Интерфейс вознаграждает
 * за уточнение, а не требует его.
 */
/**
 * Пометки, которые не мешают зайти: нет ссылки и «нечем перепроверить».
 * На зеркале они висели почти на каждой строке и кричали о нашей незрелости,
 * а не о смерти вилки. В блокеры и на экран их не пускаем.
 */
const QUIET_RULES = new Set(['no_link', 'unverifiable']);

function blockersOf(fork: Fork, input: RowInput): string[] {
  const out: string[] = [];
  for (const check of fork.checks?.unknown ?? []) out.push(check);
  // Пометки гейта: «кэф не бьётся с рынком», «цена не двигалась». Своими словами
  // от сервера — переводить их нечем и не нужно. На нашей ленте помечены единицы
  // строк, на зеркальной — восемь из десяти, и молчать о причине там значит
  // показать чужое сомнительное число как своё проверенное.
  for (const rule of fork.rl ?? []) {
    if (!QUIET_RULES.has(rule.c)) out.push(rule.t);
  }
  if (tooShort(fork, input.tempo)) out.push('такие вилки обычно живут меньше вашего темпа');
  return out;
}

/**
 * Чего нет у ИСТОЧНИКА, а не у этой строки.
 *
 * Разделение не косметическое. Замер 06.08 на зеркале: `sv` и `svGroup` пусты у
 * всех 125 строк кадра, `cap.kind` у всех ног — `assumed` без числа. То есть
 * «живучесть не измерена» и «лимит не назван» стояли на КАЖДОЙ строке ленты.
 *
 * Пометка, которая стоит везде, не сообщает ничего и стоит дорого: человек
 * перестаёт читать этот список вообще — вместе с настоящими причинами, которые
 * появляются на одной строке из десяти. Поэтому общее для всей ленты называется
 * один раз над лентой, а в строке остаётся только её собственное.
 *
 * Право вести это НЕ ослабляет: пробел остаётся пробелом и считается там же.
 */
function gapsOf(fork: Fork, legs: RowLeg[]): string[] {
  const out: string[] = [];
  for (const leg of legs) {
    if (leg.source === HAND_SOURCE) continue;
    if (leg.cap.kind === 'assumed' && leg.cap.value === null) {
      out.push(`лимит ${leg.source} не назван`);
    }
  }
  if (fork.sv === null || fork.sv === undefined) out.push('живучесть этой пары не измерена');
  return out;
}

/**
 * Успевает ли оператор к типичной вилке этой пары площадок.
 *
 * Сравнивается типичная жизнь пары, а не остаток до смерти этой строки.
 * Остаток вычитали раньше — и на живой ленте пометку получали 89% строк:
 * возраст растёт у всех, поэтому вычитание рано или поздно топит любую строку.
 * Пугать каждую строку — то же самое, что не пугать. Возраст и без того нарисован
 * полосой жизни, а эта пометка отвечает на другой вопрос: бывает ли вообще у
 * такой пары запас времени под вторую ногу. На том же кадре так помечено 22%.
 *
 * Без измеренного темпа пометки нет вовсе: чужая мерка тут не годится.
 */
export function tooShort(fork: Fork, tempo: number | null): boolean {
  if (tempo === null) return false;
  const life = typeof fork.sv === 'number' ? fork.sv : fork.svGroup;
  if (typeof life !== 'number') return false;
  return life < tempo;
}

/** Ярус строки: по деньгам, а не по проценту. Высота строки и есть сортировка. */
export type Tier = 'take' | 'ok' | 'small';

export const tierOf = (profit: number, gate: number): Tier => {
  if (profit >= gate) return 'take';
  // Нижняя граница выведена из порога, а не задана вторым регулятором: панель
  // из семи полей никто не настраивает, а один порог — настраивают.
  // Ниже отрицательного «беру от» яруса «мелочь» нет: 0.29×порог оказался бы
  // ближе к нулю, чем сам порог, и весь рабочий минус уехал бы вниз списка.
  if (gate < 0) return 'ok';
  return profit >= Math.max(50, Math.round((gate * 0.29) / 10) * 10) ? 'ok' : 'small';
};
